// src/i18n/index.js
// Multilingue de l'espace CLIENT (01/10/2026) : français, anglais, italien, espagnol, allemand.
//
// • Les textes de l'interface vivent dans `src/locales/parts/<espace>.json`, un fichier par
//   écran ou composant, au format { fr: { <espace>: {…} }, en: {…}, it: {…}, es: {…}, de: {…} }.
//   Ils sont fusionnés ici automatiquement : ajouter un écran = ajouter son fichier.
// • Les messages du SERVEUR (erreurs, événements) viennent du catalogue partagé avec l'app
//   mobile, `shared/i18n/<langue>.json`, rangé sous la clé `serveur`.
// • L'ADMIN reste en français : sur toute page /admin, la langue est forcée à 'fr' (router), sans
//   toucher au choix mémorisé du client. Le texte français de chaque clé est le texte d'origine.
import { createI18n } from 'vue-i18n';

export const LANGUES = [
  { code: 'fr', nom: 'Français' },
  { code: 'en', nom: 'English' },
  { code: 'it', nom: 'Italiano' },
  { code: 'es', nom: 'Español' },
  { code: 'de', nom: 'Deutsch' }
];
const CODES = LANGUES.map(l => l.code);
const CLE = 'langue';

// Textes de l'interface : fusion de tous les fichiers de src/locales/parts.
const messages = Object.fromEntries(CODES.map(c => [c, {}]));
const parts = require.context('../locales/parts', false, /\.json$/);
for (const f of parts.keys()) {
  const contenu = parts(f);
  for (const c of CODES) Object.assign(messages[c], contenu[c] || {});
}
// Catalogue du serveur (partagé avec l'app mobile).
const serveur = require.context('../../../shared/i18n', false, /^\.\/(fr|en|it|es|de)\.json$/);
for (const f of serveur.keys()) {
  const c = f.match(/(fr|en|it|es|de)\.json$/)[1];
  messages[c].serveur = serveur(f);
}

// Le choix mémorisé du client, sinon la langue du navigateur si on la propose, sinon le français.
function langueMemorisee() {
  try {
    const l = localStorage.getItem(CLE);
    if (CODES.includes(l)) return l;
  } catch (e) { /* stockage indisponible */ }
  const nav = (navigator.language || 'fr').slice(0, 2).toLowerCase();
  return CODES.includes(nav) ? nav : 'fr';
}

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: langueMemorisee(),
  fallbackLocale: 'fr',
  messages,
  missingWarn: false,
  fallbackWarn: false
});

const appliquer = (code) => {
  i18n.global.locale.value = code;
  try { document.documentElement.lang = code; } catch (e) { /* hors navigateur */ }
};

// Choix explicite du client (sélecteur) : mémorisé.
export function choisirLangue(code) {
  if (!CODES.includes(code)) return;
  try { localStorage.setItem(CLE, code); } catch (e) { /* stockage indisponible */ }
  appliquer(code);
}

// Appelé à chaque navigation : français imposé sur l'admin, langue du client ailleurs.
export function langueSelonRoute(chemin) {
  appliquer(String(chemin || '').startsWith('/admin') ? 'fr' : langueMemorisee());
}

export const langueCourante = () => i18n.global.locale.value;
